import React from 'react';
import { Formik, Field } from 'formik';
import { Grid, Typography, Box } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import PageLoader from '../../components/PageLoader';
import LoadingButton from '../../components/LoadingButton';
import FormSelect from '../../components/FormSelect';
import FormRadio from '../../components/FormRadio';
import PromptNotification from '../../components/PromptComponent';
import FormBody from '../../components/FormBody';
import { currentCompany } from '../../../actionTypes';
import { enumTypes } from '../../../utils/constants/formBuilder';
import { yesnoOptions } from '../../../utils/constants/formConstats';

const createInitialValues = (questions, answersLib) =>
  questions.reduce((acc, item) => {
    if (!item.isActive) return acc;
    const storedValue = answersLib[item.id]?.text ?? '';
    const initialValue = storedValue ? (isNaN(storedValue) ? storedValue : parseInt(storedValue, 10)) : '';
    return {
      ...acc,
      [`${item.id}`]: initialValue,
    };
  }, {});

const createValidationSchema = questions => {
  const shape = questions.reduce((acc, item) => {
    if (!item.isActive) return acc;
    if (item.isRequired) {
      return {
        ...acc,
        [item.id]: Yup.string().required('This field is required'),
      };
    }
    return acc;
  }, {});
  return Yup.object().shape(shape);
};

const WorkingHoursQuestionsForm = ({ ownerCompanyId, contractorCompanyId }) => {
  const dispatch = useDispatch();

  const { questions, answers, loading, saving, success, error } = useSelector(
    store => store.currentCompany.contractorWorkingHoursAnswers,
  );

  if (loading) {
    return <PageLoader />;
  }

  if (!questions || questions.length === 0) {
    return null;
  }

  const activeQuestions = questions.filter(q => q.isActive);
  if (activeQuestions.length === 0) {
    return null;
  }

  const answersLib = answers.reduce((acc, item) => ({ ...acc, [item.formBuilderFieldId]: item }), {});

  const getAnswers = values =>
    Object.keys(values).map(fieldId => ({
      id: answersLib[fieldId] ? answersLib[fieldId].id : 0,
      formBuilderFieldId: parseInt(fieldId, 10),
      text: typeof values[fieldId] === 'number' ? values[fieldId].toString() : values[fieldId],
    }));

  const handleSubmit = values => {
    dispatch({
      type: currentCompany.contractorWorkingHoursAnswers.create.start,
      payload: { ownerCompanyId, contractorCompanyId, values: getAnswers(values) },
    });
  };

  return (
    <Formik
      initialValues={createInitialValues(activeQuestions, answersLib)}
      onSubmit={handleSubmit}
      validationSchema={createValidationSchema(activeQuestions)}
      enableReinitialize
    >
      {props => (
        <>
          <PromptNotification formStatus={props.dirty} />

          <Box mt={3} mb={2}>
            <Typography variant="h6" gutterBottom>
              Additional Questions
            </Typography>
          </Box>

          <FormBody spacing={2} loading={saving}>
            {activeQuestions.map(question => (
              <Grid item xs={12} key={question.id}>
                <Typography variant="body2" gutterBottom>
                  {question.text}
                  {question.isRequired && <span style={{ color: 'red' }}> *</span>}
                </Typography>
                {question.type === enumTypes.Dropdown && (
                  <Field
                    name={`${question.id}`}
                    component={FormSelect}
                    options={question.options.map(opt => ({
                      label: opt.text,
                      value: opt.id,
                    }))}
                    placeholder="Select an option"
                  />
                )}
                {question.type === enumTypes.YesNo && (
                  <Field
                    name={`${question.id}`}
                    component={FormRadio}
                    options={
                      question.options && question.options.length > 0
                        ? question.options.map(opt => ({
                            label: opt.text,
                            value: opt.id,
                          }))
                        : yesnoOptions
                    }
                  />
                )}
              </Grid>
            ))}

            <Grid item xs={12} alignContent="flex-end">
              <LoadingButton
                disabled={!props.dirty || !props.isValid}
                text="Save Answers"
                loading={saving}
                success={success}
                error={error}
                onClick={props.handleSubmit}
                skipCheckPQF
              />
            </Grid>
          </FormBody>
        </>
      )}
    </Formik>
  );
};

export default WorkingHoursQuestionsForm;
